/**
 * Ghoshna Console — application shell controller.
 *
 * Owns navigation chrome only: breadcrumbs, sidebar state, content width,
 * which date control is visible, and the mobile drawer.
 *
 * It never performs an action itself. Every navigation proxies a click to the
 * per-view control that already carries the behaviour — its confirm dialogs,
 * its state resets, its Firestore reads. That is what makes "one back button,
 * one sign out" possible without rewriting a single handler.
 */

const VIEW_META = {
  dateSelectionView: {
    crumbs: [], nav: 'navWorkspace',
    width: 'default', date: 'none', back: null, signOut: 'signOutDateView',
  },
  dataEntryView: {
    crumbs: ['News Articles', 'Articles'], nav: 'navArticles', sub: 'navStepArticles',
    width: 'default', date: 'entry', stepper: true,
    back: 'backToDateFromEntry', signOut: 'signOutEntryView',
  },
  translationOptionsView: {
    crumbs: ['News Articles', 'Languages'], nav: 'navArticles', sub: 'navStepLanguages',
    width: 'default', date: 'static', stepper: true,
    back: 'backToEntryFromTranslation', signOut: 'signOutTranslationView',
  },
  articleTranslateView: {
    crumbs: ['News Articles', 'Translate'], nav: 'navArticles',
    width: 'default', date: 'static',
    back: 'backToArticlesList', signOut: 'signOutArticleTranslateView',
  },
  reviewEditView: {
    crumbs: ['News Articles', 'Review'], nav: 'navArticles', sub: 'navStepReview',
    width: 'default', date: 'static', stepper: true,
    back: 'backToTranslationFromReview', signOut: 'signOutReviewView',
    actions: ['refreshAllArticlesBtn'],
  },
  giftNiftyView: {
    crumbs: ['Gift Nifty'], nav: 'navGiftNifty',
    width: 'default', date: 'static',
    back: 'backToDateFromGiftNifty', signOut: 'signOutGiftNiftyView',
  },
  iplView: {
    crumbs: ['IPL Manager'], nav: 'navIpl',
    width: 'default', date: 'static',
    back: 'backToDateFromIpl', signOut: 'signOutIplView',
  },
  gmImagesView: {
    crumbs: ['Good Morning Images'], nav: 'navGm',
    width: 'default', date: 'gm',
    back: 'backToDateFromGm', signOut: 'signOutGmView',
  },
  // A finished task. No back, no sign-out proxy and no stepper: the page states
  // what was saved and carries its own two actions.
  submittedView: {
    crumbs: [], nav: null,
    width: 'narrow', date: 'static',
    back: null, signOut: null,
  },
};

const DATE_SLOTS = { none: [], static: ['topbarDateStatic'], entry: ['topbarDateEntry'], gm: ['topbarDateGm'] };
const ALL_DATE_SLOTS = ['topbarDateStatic', 'topbarDateEntry', 'topbarDateGm'];
const ALL_ACTIONS = ['refreshAllArticlesBtn'];

/* The news pipeline, in order. `articleTranslateView` is a detour off
   Articles, not a stage, so it never appears in the stepper. */
const STEP_ORDER = ['dataEntryView', 'translationOptionsView', 'reviewEditView'];
const NEWS_VIEWS = [...STEP_ORDER, 'articleTranslateView'];

let currentViewId = null;
let lastGating = null;

const $ = (id) => document.getElementById(id);
const click = (id) => { const el = id && $(id); if (el) el.click(); return !!el; };

function stepIndex(viewId) {
  if (viewId === 'articleTranslateView') return 0; // a detour off Articles
  return STEP_ORDER.indexOf(viewId);
}

/* ---------- Chrome ---------------------------------------------------------- */

function renderCrumbs(crumbs) {
  const nav = $('appCrumbs');
  if (!nav) return;
  nav.innerHTML = crumbs
    .map((label, i) => {
      const last = i === crumbs.length - 1;
      return `<span class="crumb"${last ? ' aria-current="page"' : ''}>${label}</span>`;
    })
    .join('<span class="crumb__sep">/</span>');
}

function renderStepper(viewId) {
  const bar = $('appStepper');
  if (!bar) return;
  const meta = VIEW_META[viewId];
  if (!meta || !meta.stepper) { bar.hidden = true; return; }
  bar.hidden = false;

  const here = stepIndex(viewId);
  const labels = ['Date', 'Articles', 'Languages', 'Review'];
  // Step 1 (Date) is always behind you once you are in the pipeline.
  const states = ['done', ...STEP_ORDER.map((_, i) => (i < here ? 'done' : i === here ? 'current' : 'todo'))];

  const chevron = '<span class="step__arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg></span>';
  bar.innerHTML = labels
    .map((label, i) => `<span class="step" data-state="${states[i]}"><span class="step__num">${i + 1}</span>${label}</span>`)
    .join(chevron) + '<span class="stepper__hint" id="appStepperHint"></span>';
}

/** Reflect the active view across the whole shell. */
export function syncShell(viewId) {
  const meta = VIEW_META[viewId];
  if (!meta) return;
  currentViewId = viewId;

  renderCrumbs(meta.crumbs);
  renderStepper(viewId);

  const inner = $('appContent');
  if (inner) inner.setAttribute('data-width', meta.width || 'default');

  document.querySelectorAll('.nav__item').forEach((el) => el.removeAttribute('aria-current'));
  document.querySelectorAll('.nav__subitem').forEach((el) => el.removeAttribute('aria-current'));
  if (meta.nav && $(meta.nav)) $(meta.nav).setAttribute('aria-current', 'page');
  if (meta.sub && $(meta.sub)) $(meta.sub).setAttribute('aria-current', 'page');

  // The pipeline steps only make sense while inside the pipeline.
  const sub = $('navArticlesSub');
  if (sub) sub.hidden = !NEWS_VIEWS.includes(viewId);

  // You cannot be on a page whose sidebar entry is folded away.
  const group = $('navDataFeeder');
  if (group && meta.nav) group.setAttribute('aria-expanded', 'true');

  const back = $('shellBack');
  if (back) back.hidden = !meta.back;

  const wanted = DATE_SLOTS[meta.date] || [];
  ALL_DATE_SLOTS.forEach((id) => { if ($(id)) $(id).hidden = !wanted.includes(id); });

  const acts = meta.actions || [];
  ALL_ACTIONS.forEach((id) => { if ($(id)) $(id).hidden = !acts.includes(id); });

  // Reachability depends on where you are, so recompute on every hop.
  if (lastGating) setStepAvailability(lastGating);

  closeDrawer();
}

/* ---------- Drawer ----------------------------------------------------------- */
function openDrawer() {
  document.body.setAttribute('data-nav-open', 'true');
  document.querySelectorAll('[data-nav-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
}
function closeDrawer() {
  document.body.removeAttribute('data-nav-open');
  document.querySelectorAll('[data-nav-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
}

/* ---------- Step navigation ---------------------------------------------------
   The pipeline is a wizard. Clicking a step only ever moves BACKWARD to work
   already done; forward movement stays with each page's primary action, so its
   validation cannot be skipped. Each hop reuses that view's own back control. */
/**
 * Wait until the active view satisfies `test`.
 *
 * Back handlers are async — several `await` a confirm dialog. Navigation has
 * to wait for that to resolve, or it lands on the next page while the dialog
 * is still open and the operator's answer then resets state underneath them.
 * Resolves false on timeout or if the operator cancelled.
 */
function waitForView(test, timeoutMs = 15000) {
  return waitUntil(() => test(currentViewId), timeoutMs);
}

/** Poll until `test()` is true. Resolves false on timeout. */
function waitUntil(test, timeoutMs = 15000) {
  return new Promise((resolve) => {
    if (test()) return resolve(true);
    const started = Date.now();
    const tick = () => {
      if (test()) return resolve(true);
      if (Date.now() - started > timeoutMs) return resolve(false);
      setTimeout(tick, 60);
    };
    tick();
  });
}

/* One navigation at a time. Without this, a second click while a confirm is
   pending stacks another listener on the same dialog buttons and the back
   handler runs more than once for a single answer. */
let navBusy = false;

async function goToView(target) {
  if (navBusy || target === currentViewId) return;
  if (!navigate) return;
  navBusy = true;
  try {
    // One question about the page being left, then go. This used to walk back
    // one step at a time via each page's Back button, which meant leaving
    // Review asked twice, and leaving for the Workspace from any nested page
    // never got there at all — the walk only ever went up one level.
    const outcome = await leaveGuard(currentViewId);
    if (outcome !== 'go') return;   // 'stay', or a save that navigated itself
    navigate(target);
  } finally {
    navBusy = false;
  }
}

/** Open a task. Routes via the workspace so its date checks still run. */
async function goToTask(value) {
  if (navBusy) return;
  if (!navigate) return;
  navBusy = true;
  try {
    if (currentViewId !== 'dateSelectionView') {
      const outcome = await leaveGuard(currentViewId);
      if (outcome !== 'go') return;
      navigate('dateSelectionView');
      const reached = await waitForView((v) => v === 'dateSelectionView');
      if (!reached) return;
    }
    // Landing on the workspace kicks off a re-read of the date, and that
    // disables Continue for as long as it runs. Ticking the radio and clicking
    // immediately therefore hit a disabled button and did nothing — from a task
    // page, picking another task in the sidebar dropped you on the workspace
    // and stopped there. Wait for the button to come back before using it.
    const cont = $('continueBtn');
    if (!cont) return;
    if (!(await waitUntil(() => !cont.disabled, 12000))) return;

    const radio = document.querySelector(`input[name="routeSelector"][value="${value}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    cont.click();
  } finally {
    navBusy = false;
  }
}

/* ---------- Navigation services ------------------------------------------------
   The shell owns chrome, not behaviour. main.js supplies the two things it
   cannot know: how to show a view, and how to ask before leaving one. */
let navigate = null;
let leaveGuard = async () => 'go';

/**
 * @param {(viewId: string) => void} showView
 * @param {(viewId: string) => Promise<boolean>} canLeave
 */
export function setNavigationServices({ showView, canLeave }) {
  if (typeof showView === 'function') navigate = showView;
  if (typeof canLeave === 'function') leaveGuard = canLeave;
}

/* ---------- Wiring ------------------------------------------------------------ */
export function initShell() {
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nav-toggle]')) {
      document.body.hasAttribute('data-nav-open') ? closeDrawer() : openDrawer();
      return;
    }
    if (e.target.closest('[data-nav-close]')) { closeDrawer(); return; }

    const group = e.target.closest('#navDataFeeder');
    if (group) {
      e.preventDefault();
      group.setAttribute('aria-expanded', group.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      return;
    }

    const taskBtn = e.target.closest('[data-nav-task]');
    if (taskBtn) {
      e.preventDefault();
      goToTask(taskBtn.dataset.navTask);
      return;
    }

    const viewBtn = e.target.closest('[data-nav-view]');
    if (viewBtn) {
      e.preventDefault();
      if (viewBtn.disabled) return;
      const target = viewBtn.dataset.navView;
      if (target === currentViewId) { closeDrawer(); return; }
      goToView(target);
      return;
    }

    // A task card opens its task directly — one click, no separate confirm
    // step. The card is a <label>, and a label click also forwards a second
    // click from its control; both bubble here, so without this guard the
    // Continue handler ran twice and every route toast appeared twice.
    if (e.target.closest('input[name="routeSelector"]')) return;

    const card = e.target.closest('.taskcard');
    if (card && !navBusy) {
      const input = card.querySelector('input[name="routeSelector"]');
      if (!input) return;
      setTimeout(() => {
        if (!input.checked || navBusy) return;
        const btn = $('continueBtn');
        if (btn && !btn.disabled) btn.click();
      }, 0);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.hasAttribute('data-nav-open')) closeDrawer();
  });

  const back = $('shellBack');
  if (back) back.addEventListener('click', () => {
    const meta = VIEW_META[currentViewId];
    if (meta && meta.back) click(meta.back);
  });

  const signOut = $('sidebarSignOut');
  if (signOut) signOut.addEventListener('click', () => {
    const meta = VIEW_META[currentViewId];
    if (!(meta && meta.signOut && click(meta.signOut))) click('signOutDateView');
  });
}

/* ---------- State reflected from main.js -------------------------------------- */

/** Which pipeline steps are reachable, and why not when they aren't. */
export function setStepAvailability(opts = {}) {
  lastGating = opts;
  const { articles = false, languages = false, review = false, reason = {} } = opts;
  const here = stepIndex(currentViewId);
  const behind = (i) => here !== -1 && i < here;

  const map = {
    navStepArticles:  { on: articles  && behind(0), why: reason.articles },
    navStepLanguages: { on: languages && behind(1), why: reason.languages },
    navStepReview:    { on: review    && behind(2), why: reason.review },
  };
  Object.entries(map).forEach(([id, { on, why }]) => {
    const el = $(id);
    if (!el) return;
    el.disabled = !on;
    if (why && !on) el.title = why; else el.removeAttribute('title');
  });

  const hint = $('appStepperHint');
  if (hint) hint.textContent = opts.stepperHint || '';
}


export function setShellUser(email) {
  const nameEl = $('sidebarUserName');
  const initEl = $('sidebarUserInitial');
  if (nameEl) nameEl.textContent = email || 'Signed in';
  if (initEl) initEl.textContent = (email || '?').trim().charAt(0).toUpperCase();
}
